import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    DownloadCloud,
    Search,
    Eye,
    Table as TableIcon,
    LayoutGrid,
    Trash2,
    AlertTriangle,
    X,
    CheckSquare,
    Square,
    MinusSquare,
    Check,
} from "lucide-react";
import {
    getResponses,
    buildExportUrl,
    bulkDeleteResponses,
} from "../adminApi";
import { Pill } from "../components/Pill";
import { Button } from "../../components/shared/Button";
import { ResponseDrawer } from "../components/ResponseDrawer";
import { individualPriceLabel } from "../labels";
import { ARCHETYPE_TILE } from "../archetypes";

const PERSONAS = ["", "student", "professional", "career_services", "employer"];
const PAINS = ["", "direction", "stuck", "confidence", "external", "none"];
const WAITLISTS = ["", "yes_pay", "yes_free", "maybe", "no"];

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return `${d.toLocaleDateString(undefined, { month: "short", day: "2-digit" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
    } catch {
        return iso;
    }
}

function fmtTileDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "2-digit",
            year: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

function scoreColor(s) {
    if (s == null) return "#6B7FA3";
    if (s >= 70) return "#10B981";
    if (s >= 50) return "#F59E0B";
    return "#EF4444";
}

export default function AdminResponses() {
    const { includeSeed, refreshKey } = useOutletContext();
    const [persona, setPersona] = useState("");
    const [pain, setPain] = useState("");
    const [waitlist, setWaitlist] = useState("");
    const [hasEmail, setHasEmail] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(25);
    const [view, setView] = useState("table"); // 'table' | 'snapshots'
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [drawerId, setDrawerId] = useState(null);

    // Selection — Set<id>. Persists across pagination on the same filter set.
    // Cleared on filter / search / include-seed / view-toggle (handled below).
    const [selected, setSelected] = useState(() => new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [toast, setToast] = useState(null);

    const params = useMemo(
        () => ({
            page,
            limit,
            persona: persona || undefined,
            pain: pain || undefined,
            waitlist: waitlist || undefined,
            has_email: hasEmail ? true : undefined,
            search: search.trim() || undefined,
            include_seed: includeSeed,
        }),
        [page, limit, persona, pain, waitlist, hasEmail, search, includeSeed],
    );

    // Clear selection whenever any filter (not just page) changes.
    useEffect(() => {
        setSelected(new Set());
    }, [persona, pain, waitlist, hasEmail, search, includeSeed]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getResponses(params)
            .then((d) => alive && setData(d))
            .catch(() => alive && setData({ items: [], total: 0 }))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [params, refreshKey]);

    // Toast auto-dismiss
    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(() => setToast(null), 4500);
        return () => clearTimeout(t);
    }, [toast]);

    const totalPages = Math.max(1, Math.ceil(data.total / limit));

    function exportCsv() {
        const url = buildExportUrl({ ...params, page: undefined, limit: undefined });
        window.open(url + "&" + Date.now(), "_blank", "noopener");
    }

    function refreshList() {
        return getResponses(params).then(setData);
    }

    function toggleId(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function toggleAllOnPage() {
        const ids = data.items.map((r) => r.id);
        const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
        setSelected((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                ids.forEach((id) => next.delete(id));
            } else {
                ids.forEach((id) => next.add(id));
            }
            return next;
        });
    }

    function clearSelection() {
        setSelected(new Set());
    }

    async function performBulkDelete() {
        const ids = Array.from(selected);
        const res = await bulkDeleteResponses(ids);
        setBulkOpen(false);
        if (!res.ok) {
            setToast({
                kind: "error",
                message: `Bulk delete failed: ${res.error}`,
            });
            return; // selection preserved so they can retry
        }
        const deleted = res.data?.deleted || 0;
        clearSelection();
        setToast({ kind: "success", message: `Deleted ${deleted} responses.` });
        await refreshList();
        // If the current page is now empty and not page 1, go back one page.
        // We can't know the new total without re-reading, but the next refetch
        // updates `data.total`. We refresh then re-evaluate via effect below.
    }

    // After a refresh, if the current page is empty AND not page 1, step back.
    useEffect(() => {
        if (!loading && data.items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [loading, data.items.length, page]);

    const idsOnPage = data.items.map((r) => r.id);
    const allOnPageSelected =
        idsOnPage.length > 0 && idsOnPage.every((id) => selected.has(id));
    const someOnPageSelected =
        idsOnPage.some((id) => selected.has(id)) && !allOnPageSelected;

    return (
        <div className="space-y-6 relative" data-testid="admin-responses">
            {/* Sticky bulk-action bar */}
            <BulkActionBar
                count={selected.size}
                onClear={clearSelection}
                onDelete={() => setBulkOpen(true)}
            />

            {/* Filters */}
            <div className="rounded-2xl border border-klareet-bor bg-klareet-card p-5 flex flex-wrap items-center gap-3">
                <Select
                    label="Persona"
                    value={persona}
                    onChange={(v) => {
                        setPage(1);
                        setPersona(v);
                    }}
                    options={PERSONAS}
                    testId="filter-persona"
                />
                <Select
                    label="Pain"
                    value={pain}
                    onChange={(v) => {
                        setPage(1);
                        setPain(v);
                    }}
                    options={PAINS}
                    testId="filter-pain"
                />
                <Select
                    label="Waitlist"
                    value={waitlist}
                    onChange={(v) => {
                        setPage(1);
                        setWaitlist(v);
                    }}
                    options={WAITLISTS}
                    testId="filter-waitlist"
                />
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-klareet-bor bg-klareet-NAVY text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={hasEmail}
                        onChange={(e) => {
                            setPage(1);
                            setHasEmail(e.target.checked);
                        }}
                        className="accent-klareet-T2"
                        data-testid="filter-has-email"
                    />
                    <span className="text-klareet-MUT">Has email</span>
                </label>
                <div className="relative flex-1 min-w-[220px]">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-klareet-MUT"
                    />
                    <input
                        value={search}
                        onChange={(e) => {
                            setPage(1);
                            setSearch(e.target.value);
                        }}
                        placeholder="Search email · name · open answer"
                        className="w-full pl-9 pr-3 py-2 rounded-xl border border-klareet-bor bg-klareet-NAVY text-sm text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40"
                        data-testid="filter-search"
                    />
                </div>
                <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                    <DownloadCloud size={16} />
                    Export CSV
                </Button>
            </div>

            {/* View toggle */}
            <div className="flex items-center justify-between">
                <div
                    className="inline-flex rounded-full border border-klareet-bor bg-klareet-card p-1"
                    data-testid="view-toggle"
                >
                    <ToggleBtn
                        active={view === "table"}
                        onClick={() => setView("table")}
                        icon={<TableIcon size={14} />}
                        label="Table"
                        testId="view-toggle-table"
                    />
                    <ToggleBtn
                        active={view === "snapshots"}
                        onClick={() => setView("snapshots")}
                        icon={<LayoutGrid size={14} />}
                        label="Snapshots"
                        testId="view-toggle-snapshots"
                    />
                </div>
                <div className="text-xs font-mono text-klareet-MUT">
                    {data.total} total
                </div>
            </div>

            {view === "table" ? (
                <ResponsesTable
                    items={data.items}
                    loading={loading}
                    selected={selected}
                    onToggleId={toggleId}
                    onToggleAllOnPage={toggleAllOnPage}
                    allOnPageSelected={allOnPageSelected}
                    someOnPageSelected={someOnPageSelected}
                    onOpen={(id) => setDrawerId(id)}
                />
            ) : (
                <SnapshotsGrid
                    items={data.items}
                    loading={loading}
                    selected={selected}
                    onToggleId={toggleId}
                    onOpen={(id) => setDrawerId(id)}
                />
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between">
                <div className="text-xs font-mono text-klareet-MUT">
                    Showing {(data.items.length && (page - 1) * limit + 1) || 0}
                    –{(page - 1) * limit + data.items.length} of {data.total}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        data-testid="page-prev"
                    >
                        <ChevronLeft size={14} />
                        Prev
                    </Button>
                    <span className="text-xs font-mono text-klareet-MUT px-2">
                        Page {page} of {totalPages}
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        data-testid="page-next"
                    >
                        Next
                        <ChevronRight size={14} />
                    </Button>
                </div>
            </div>

            <ResponseDrawer
                responseId={drawerId}
                open={Boolean(drawerId)}
                onClose={() => setDrawerId(null)}
                onDeleted={() => refreshList()}
            />

            {bulkOpen ? (
                <BulkDeleteModal
                    selected={selected}
                    items={data.items}
                    onCancel={() => setBulkOpen(false)}
                    onConfirm={performBulkDelete}
                />
            ) : null}

            <ToastBanner toast={toast} onClose={() => setToast(null)} />
        </div>
    );
}

function BulkActionBar({ count, onClear, onDelete }) {
    return (
        <AnimatePresence>
            {count > 0 ? (
                <motion.div
                    key="bulk-bar"
                    initial={{ y: -32, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -32, opacity: 0 }}
                    transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                    className="sticky top-0 z-30 -mx-5 md:-mx-8 -mt-8 mb-2 px-5 md:px-8 py-3 border-b border-klareet-T/40 bg-klareet-T/10 backdrop-blur"
                    data-testid="bulk-action-bar"
                >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <span
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-klareet-T text-white text-xs font-mono font-bold"
                                data-testid="bulk-count"
                            >
                                {count}
                            </span>
                            <div>
                                <div className="text-sm font-medium text-klareet-TEXT">
                                    {count} selected
                                </div>
                                <div className="text-[11px] text-klareet-MUT font-mono">
                                    Tip: selections persist across pages.
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-xs text-klareet-T2 hover:text-klareet-TEXT transition-colors"
                                data-testid="bulk-clear-btn"
                            >
                                Clear selection
                            </button>
                            <button
                                type="button"
                                onClick={onDelete}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-RED text-klareet-RED text-sm hover:bg-klareet-RED/10 transition-colors"
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 size={14} />
                                Delete {count} selected
                            </button>
                        </div>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function BulkDeleteModal({ selected, items, onCancel, onConfirm }) {
    const [typed, setTyped] = useState("");
    const [busy, setBusy] = useState(false);
    const ids = Array.from(selected);
    const N = ids.length;

    // Build a small preview from the items currently loaded (subset of all
    // selected — selections persist across pages so we may not have every
    // row in `items`). Show whatever we can of the first 5 by id-order.
    const idToItem = new Map(items.map((i) => [i.id, i]));
    const previewRows = ids.slice(0, 5).map((id) => idToItem.get(id) || { id });
    const moreCount = Math.max(0, N - previewRows.length);

    async function go() {
        if (typed !== "DELETE") return;
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
        }
    }

    return (
        <motion.div
            key="bulk-delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] grid place-items-center px-5 bg-klareet-NAVY/85 backdrop-blur-sm"
        >
            <div
                className="max-w-lg w-full rounded-3xl border border-klareet-RED/40 bg-klareet-card p-6"
                data-testid="bulk-delete-modal"
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight text-klareet-TEXT flex items-center gap-2">
                        <AlertTriangle size={18} className="text-klareet-RED" />
                        Permanently delete {N} responses?
                    </h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="text-klareet-MUT hover:text-klareet-TEXT disabled:opacity-50"
                        data-testid="bulk-modal-x"
                    >
                        <X size={18} />
                    </button>
                </div>
                <p className="mt-3 text-sm text-klareet-MUT leading-relaxed">
                    This cannot be undone. Type{" "}
                    <span className="font-mono text-klareet-RED">DELETE</span>{" "}
                    to confirm.
                </p>

                {N >= 5 ? (
                    <div className="mt-4 rounded-2xl border border-klareet-bor bg-klareet-NAVY/40 p-4 max-h-44 overflow-y-auto">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-2">
                            Selection preview
                        </div>
                        <ul className="space-y-1.5 text-xs">
                            {previewRows.map((r) => (
                                <li
                                    key={r.id}
                                    className="flex items-center gap-2 text-klareet-TEXT/85"
                                >
                                    <span className="font-mono text-klareet-MUT">
                                        {r.created_at
                                            ? fmtDate(r.created_at)
                                            : r.id.slice(0, 8) + "…"}
                                    </span>
                                    {r.persona ? (
                                        <Pill value={r.persona} kind="persona" />
                                    ) : null}
                                    {r.pain ? (
                                        <Pill value={r.pain} kind="pain" />
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                        {moreCount > 0 ? (
                            <div className="mt-2 text-[11px] font-mono text-klareet-MUT">
                                …and {moreCount} more
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <input
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="DELETE"
                    className="mt-4 w-full px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-NAVY font-mono text-klareet-TEXT outline-none focus:border-klareet-RED focus:ring-2 focus:ring-klareet-RED/40"
                    data-testid="bulk-delete-input"
                    disabled={busy}
                />
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="inline-flex items-center px-4 py-2 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-TEXT text-sm disabled:opacity-50"
                        data-testid="bulk-modal-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={go}
                        disabled={typed !== "DELETE" || busy}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-klareet-RED text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                        data-testid="bulk-modal-confirm"
                    >
                        <Trash2 size={14} />
                        {busy ? "Deleting…" : `Delete ${N} forever`}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

function ToastBanner({ toast, onClose }) {
    return (
        <AnimatePresence>
            {toast ? (
                <motion.div
                    key="toast"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.22 }}
                    className={`fixed bottom-6 right-6 z-[90] max-w-md rounded-2xl border px-4 py-3 backdrop-blur shadow-lg flex items-start gap-3 ${
                        toast.kind === "success"
                            ? "border-klareet-GREEN/40 bg-klareet-GREEN/10 text-klareet-GREEN"
                            : "border-klareet-RED/40 bg-klareet-RED/10 text-klareet-RED"
                    }`}
                    data-testid="bulk-toast"
                    role="status"
                >
                    {toast.kind === "success" ? (
                        <Check size={16} className="mt-0.5 shrink-0" />
                    ) : (
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1 text-sm">{toast.message}</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="opacity-70 hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <X size={14} />
                    </button>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function ToggleBtn({ active, onClick, icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs transition-colors ${
                active
                    ? "bg-klareet-T text-white"
                    : "text-klareet-MUT hover:text-klareet-TEXT"
            }`}
            data-testid={testId}
        >
            {icon}
            {label}
        </button>
    );
}

function Th({ children, align = "left", className = "" }) {
    return (
        <th
            className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"} font-medium text-klareet-MUT ${className}`}
        >
            {children}
        </th>
    );
}

function Select({ label, value, onChange, options, testId }) {
    return (
        <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-klareet-bor bg-klareet-NAVY text-sm">
            <span className="text-[11px] uppercase tracking-[0.16em] text-klareet-MUT font-mono">
                {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="bg-transparent outline-none text-klareet-TEXT capitalize"
                data-testid={testId}
            >
                {options.map((opt) => (
                    <option
                        key={opt || "all"}
                        value={opt}
                        className="bg-klareet-NAVY text-klareet-TEXT"
                    >
                        {opt ? opt.replace(/_/g, " ") : "All"}
                    </option>
                ))}
            </select>
        </label>
    );
}

/**
 * Tri-state checkbox button — empty / checked / indeterminate.
 * Pure CSS + Lucide; we don't use the native input so we can render the
 * indeterminate icon (Lucide MinusSquare) consistently.
 */
function TriCheck({ state, onClick, label, testId }) {
    const Icon =
        state === "checked"
            ? CheckSquare
            : state === "some"
              ? MinusSquare
              : Square;
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                state === "empty"
                    ? "text-klareet-MUT hover:text-klareet-T2"
                    : "text-klareet-T2"
            }`}
            aria-label={label}
            data-testid={testId}
        >
            <Icon size={16} />
        </button>
    );
}

function ResponsesTable({
    items,
    loading,
    selected,
    onToggleId,
    onToggleAllOnPage,
    allOnPageSelected,
    someOnPageSelected,
    onOpen,
}) {
    const headerState = allOnPageSelected
        ? "checked"
        : someOnPageSelected
          ? "some"
          : "empty";
    return (
        <div className="rounded-2xl border border-klareet-bor bg-klareet-card overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="responses-table">
                    <thead className="text-[11px] uppercase tracking-[0.16em] text-klareet-MUT font-mono">
                        <tr className="border-b border-klareet-bor">
                            <th className="px-3 py-3 w-10">
                                <TriCheck
                                    state={headerState}
                                    onClick={onToggleAllOnPage}
                                    label="Select all on this page"
                                    testId="select-all-page"
                                />
                            </th>
                            <Th>Date</Th>
                            <Th>Persona</Th>
                            <Th>Pain</Th>
                            <Th>Appeal</Th>
                            <Th>Pricing</Th>
                            <Th>Waitlist</Th>
                            <Th>Email</Th>
                            <Th>Name</Th>
                            <Th>Refs</Th>
                            <Th align="right">Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={11} className="p-10 text-center text-klareet-MUT">
                                    Loading…
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={11} className="p-10 text-center text-klareet-MUT">
                                    No responses match these filters.
                                </td>
                            </tr>
                        ) : (
                            items.map((r) => (
                                <ResponseRow
                                    key={r.id}
                                    r={r}
                                    isSelected={selected.has(r.id)}
                                    onToggle={() => onToggleId(r.id)}
                                    onOpen={() => onOpen(r.id)}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ResponseRow({ r, isSelected, onToggle, onOpen }) {
    return (
        <tr
            className={`border-b border-klareet-bor cursor-pointer transition-colors ${
                isSelected ? "bg-klareet-T/5" : "hover:bg-klareet-NAVY/30"
            }`}
            onClick={onOpen}
            data-testid={`row-${r.id}`}
        >
            <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                <TriCheck
                    state={isSelected ? "checked" : "empty"}
                    onClick={onToggle}
                    label="Select row"
                    testId={`select-${r.id}`}
                />
            </td>
            <td className="px-4 py-3 font-mono text-xs text-klareet-MUT whitespace-nowrap">
                {fmtDate(r.created_at)}
            </td>
            <td className="px-4 py-3">
                <Pill value={r.persona} kind="persona" />
            </td>
            <td className="px-4 py-3">
                <Pill value={r.pain} kind="pain" />
            </td>
            <td className="px-4 py-3 font-mono tabular-nums">
                {r.klareet_appeal}
            </td>
            <td
                className="px-4 py-3 text-klareet-TEXT/85 whitespace-nowrap"
                data-testid={`row-pricing-${r.id}`}
            >
                {r.price_individual ? (
                    individualPriceLabel(r.price_individual)
                ) : (
                    <span className="text-klareet-DIM">—</span>
                )}
            </td>
            <td className="px-4 py-3">
                <Pill value={r.waitlist} kind="waitlist" />
            </td>
            <td className="px-4 py-3 max-w-[240px] truncate">
                {r.email || <span className="text-klareet-DIM">—</span>}
            </td>
            <td className="px-4 py-3 max-w-[160px] truncate">
                {r.name || <span className="text-klareet-DIM">—</span>}
            </td>
            <td className="px-4 py-3 font-mono tabular-nums">
                {r.referral_count}
                {r.free_year_earned ? (
                    <span className="ml-1 text-[10px] text-klareet-GREEN">
                        ★
                    </span>
                ) : null}
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpen();
                        }}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-T2 hover:border-klareet-T transition-colors"
                        aria-label="View case file"
                        data-testid={`view-${r.id}`}
                    >
                        <Eye size={14} />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function SnapshotsGrid({ items, loading, selected, onToggleId, onOpen }) {
    if (loading) {
        return (
            <div className="rounded-2xl border border-klareet-bor bg-klareet-card p-16 grid place-items-center">
                <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="rounded-2xl border border-klareet-bor bg-klareet-card p-12 text-center text-klareet-MUT">
                No snapshots match these filters.
            </div>
        );
    }
    return (
        <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="snapshots-grid"
        >
            {items.map((r) => {
                const arch = ARCHETYPE_TILE(r);
                const isSelected = selected.has(r.id);
                return (
                    <button
                        key={r.id}
                        type="button"
                        onClick={() => onOpen(r.id)}
                        className={`relative text-left rounded-2xl border p-5 transition-all group ${
                            isSelected
                                ? "border-klareet-T bg-klareet-T/5"
                                : "border-klareet-bor bg-klareet-card hover:border-klareet-T hover:bg-klareet-NAVY/30"
                        }`}
                        data-testid={`snapshot-tile-${r.id}`}
                    >
                        <div className="absolute top-3 right-3">
                            <TriCheck
                                state={isSelected ? "checked" : "empty"}
                                onClick={() => onToggleId(r.id)}
                                label="Select snapshot"
                                testId={`tile-select-${r.id}`}
                            />
                        </div>
                        <div className="flex items-start justify-between gap-3 pr-8">
                            <div
                                className="text-3xl shrink-0 leading-none"
                                style={{ color: arch.color }}
                            >
                                {arch.emoji}
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[9px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                                    Score
                                </div>
                                <div
                                    className="font-mono text-2xl font-bold tabular-nums"
                                    style={{ color: scoreColor(arch.score) }}
                                >
                                    {arch.score}
                                </div>
                            </div>
                        </div>
                        <div
                            className="mt-3 text-base font-bold tracking-tight"
                            style={{ color: arch.color }}
                        >
                            {arch.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mt-1">
                            {fmtTileDate(r.created_at)}
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <Pill value={r.persona} kind="persona" />
                            <Pill value={r.pain} kind="pain" />
                        </div>
                        {r.one_thing ? (
                            <p className="mt-3 text-xs italic text-klareet-MUT line-clamp-2 leading-relaxed">
                                "{r.one_thing}"
                            </p>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}
